import React, { useState } from 'react';
import {
  Account,
  AccountCategory,
  AccountType,
  CompensationModel,
} from 'types/userTypes';
import { supportedCountries } from 'utils/country';
import { AccountFormField, AccountFormFieldError } from './Form/FormField';
import { AccountFormInput } from './Form/FormInput';
import { AccountsFormOrganizationNumber } from './Form/OrganizationNumber';
import { capitalize } from '../../../utils/strings';

interface EditAccountFormProps {
  account: Account;
  fieldErrors: AccountFormFieldError;
  isValidOrganizationNumber: (companyNumber: string) => boolean;
  syncAccount: (newValue: Partial<AccountType>) => void;
}

export const EditAccountForm = ({
  account,
  fieldErrors,
  isValidOrganizationNumber,
  ...props
}: EditAccountFormProps) => {
  const [countryCode, setCountryCode] = useState(account.countryCode);
  const [compensationModel, setCompensationModel] = useState(
    account.compensationModel
  );

  const noSpaceValidation = (value: string) => {
    return value.replace(/\s/g, '');
  };

  const phoneCodeValidation = (value: string) => {
    const newValue = noSpaceValidation(value);
    return /^\d/.test(newValue) ? `+${newValue}` : newValue;
  };

  return (
    <>
      <AccountFormField id="email" label="Email" fieldErrors={fieldErrors}>
        <AccountFormInput
          type="email"
          data-lpignore="true"
          placeholder="example@porterbuddy.com"
          name="email"
          value={account.email}
          autoComplete="none"
          syncAccount={props.syncAccount}
          validation={noSpaceValidation}
        />
      </AccountFormField>

      <AccountFormField id="country" label="Country" fieldErrors={fieldErrors}>
        <select
          onChange={event => {
            const value = event.target.value;
            setCountryCode(value);
            props.syncAccount({ countryCode: value });
          }}
          value={countryCode}
        >
          {Object.keys(supportedCountries).map(countryName => {
            const countryInfo = supportedCountries[countryName];
            return (
              <option
                key={countryName}
                value={countryInfo.countryCode.toUpperCase()}
              >
                {countryInfo.name}
              </option>
            );
          })}
        </select>
      </AccountFormField>

      <AccountFormField
        id="name"
        label="Company name"
        fieldErrors={fieldErrors}
      >
        <AccountFormInput
          type="text"
          data-lpignore="true"
          placeholder=""
          name="name"
          value={account.name}
          autoComplete="none"
          syncAccount={props.syncAccount}
        />
      </AccountFormField>

      <AccountsFormOrganizationNumber
        isValidOrganizationNumber={isValidOrganizationNumber}
        account={account}
        syncAccount={props.syncAccount}
        fieldErrors={fieldErrors}
      />

      <AccountFormField
        id="phoneCode"
        label="Phone country code"
        fieldErrors={fieldErrors}
      >
        <AccountFormInput
          type="tel"
          maxLength={20}
          data-lpignore="true"
          placeholder="+47"
          name="phoneCode"
          value={account.phoneCode}
          autoComplete="none"
          syncAccount={props.syncAccount}
          validation={phoneCodeValidation}
        />
      </AccountFormField>

      <AccountFormField
        id="phoneNumber"
        label="Phone number"
        fieldErrors={fieldErrors}
      >
        <AccountFormInput
          type="tel"
          maxLength={20}
          data-lpignore="true"
          placeholder=""
          name="phoneNumber"
          value={account.phoneNumber}
          autoComplete="none"
          syncAccount={props.syncAccount}
          validation={noSpaceValidation}
        />
      </AccountFormField>
      {account.category === AccountCategory.COURIER && (
        <AccountFormField
          id="compensationModel"
          label="Compensation Model"
          fieldErrors={fieldErrors}
        >
          <select
            value={capitalize(compensationModel)}
            onChange={event => {
              const newModel =
                CompensationModel[event.target.value.toUpperCase()];
              console.log(`setting model ${JSON.stringify(newModel)}`);
              setCompensationModel(newModel);
              props.syncAccount({ compensationModel: newModel });
            }}
          >
            {Object.keys(CompensationModel).map(model => (
              <option key={model}>
                {capitalize(CompensationModel[model])}
              </option>
            ))}
          </select>
        </AccountFormField>
      )}
    </>
  );
};
